import { createPortal } from 'react-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './ButtonSelector.module.scss';
import clx from 'classnames';
import SelectorMenu from '../SelectorMenu';
import useOutsideClick from '../../../../utils/useOutsideClick';
import useWheel from 'utils/useWheel';
import SpinnerSmall from 'Containers/Common/SpinnerSmall';

const ButtonSelector = ({
  value,
  setValue,
  options = [],
  title,
  backgroundColor,
  margin = '0',
  marginLeft = '20px',
  width = 'fit-content',
  height = 'fit-content',
  loading,
}) => {
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});

  const updateMenuPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = Math.max(rect.width, 150);
    const gap = 6;
    const estimatedHeight = Math.min((options.length || 1) * 48 + 8, 280);
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const openUp = spaceBelow < estimatedHeight && rect.top > spaceBelow;
    const left = Math.min(
      Math.max(8, rect.left),
      Math.max(8, window.innerWidth - menuWidth - 8),
    );

    setMenuStyle({
      position: 'fixed',
      left,
      width: menuWidth,
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
      zIndex: 10060,
    });
  };

  useLayoutEffect(() => {
    if (!isDropdownOpen) return undefined;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isDropdownOpen, options.length]);

  useOutsideClick(dropdownRef, (event) => {
    if (!isDropdownOpen) return;
    if (menuRef.current?.contains(event.target)) return;
    setIsDropdownOpen(false);
  });

  useWheel(dropdownRef, () => {
    if (isDropdownOpen) setIsDropdownOpen(false);
  });

  useEffect(() => {
    if (!isDropdownOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsDropdownOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDropdownOpen]);

  const selectedName = options.find((option) =>
    typeof value === 'object'
      ? JSON.stringify(option.value) === JSON.stringify(value)
      : option.value === value,
  )?.name;

  const menu =
    isDropdownOpen &&
    createPortal(
      <div
        ref={menuRef}
        className={styles.dropdown__menu}
        style={menuStyle}
        role="listbox"
      >
        <SelectorMenu
          isMultiple={false}
          setIsDropdownOpen={() => setIsDropdownOpen(false)}
          options={options}
          setValue={setValue}
          value={[value]}
        />
      </div>,
      document.body,
    );

  return (
    <div
      ref={dropdownRef}
      className={clx(styles.selector_wrapper, {
        [styles.selector_wrapper_close]: !isDropdownOpen,
      })}
      style={{
        margin,
        marginLeft,
        width,
        ...(!!backgroundColor && { backgroundColor }),
        maxWidth: width,
        minWidth: width,
        borderRadius: '10px',
      }}
    >
      {!!loading && <SpinnerSmall />}
      {title ? <div className={styles.input_title}>{title}</div> : null}
      <div
        ref={triggerRef}
        onClick={() => setIsDropdownOpen((isOpen) => !isOpen)}
        className={styles.selector_container}
        style={{
          margin,
          width,
          ...(!!backgroundColor && { backgroundColor }),
          height,
          maxWidth: width,
          minWidth: width,
        }}
        aria-expanded={isDropdownOpen}
        aria-haspopup="listbox"
      >
        <div className={styles.value}>{selectedName}</div>
        <div className={styles.arrow} />
      </div>
      {menu}
    </div>
  );
};

export default ButtonSelector;
